'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { mergePersistedPreference } from './merge-persisted-preference';
import {
  getLayoutPreference,
  saveUserLayoutPreference,
  resetUserPreference,
} from '../src/lib/supabase/layout-preferences';

/**
 * Generic per-user preference hook backed by the `layout_preferences` table
 * with `pref_type = 'user_pref'`.
 *
 * Use this for:
 *   - Help hint dismissals (`key = 'hint:incidents-dashboard'`)
 *   - Chat widget position (`key = 'chat:position'`)
 *   - Form-draft opt-in flag (`key = 'form-draft:opt-in'`)
 *   - Any other small per-user UI state that should travel between devices
 *
 * Do NOT use for:
 *   - PHI (client names, clinical notes) — use Supabase and proper tables
 *   - Frequently-changing values (e.g., draft form state) — use sessionStorage
 *   - Large payloads — visible_keys is jsonb but this is still a shared row
 *
 * Persistence: writes to `layout_preferences` scoped by
 * (tenant_id, user_id, page_key=key, pref_type='user_pref'). Tenant isolation
 * is enforced by existing RLS policies on the table.
 *
 * Debounced save — default 400ms. Reads once on mount.
 */
export interface UseUserPreferencesReturn<T> {
  /** Current value. Starts as defaultValue until first load completes. */
  value: T;
  /** Update the value (optimistic, debounced write to DB). */
  setValue: (next: T | ((prev: T) => T)) => void;
  /** Reset to defaultValue and delete the DB row. */
  reset: () => Promise<void>;
  /** True once the initial load has resolved (ready for real interaction). */
  isLoaded: boolean;
  /** True while a save is in flight. */
  isSaving: boolean;
}

export function useUserPreferences<T>(
  key: string,
  defaultValue: T,
  options: { debounceMs?: number } = {},
): UseUserPreferencesReturn<T> {
  const debounceMs = options.debounceMs ?? 400;
  const user = useAuthStore((s) => s.user);
  const tenantId = user?.tenantId ?? '';
  const userId = user?.id ?? '';

  const [value, setInternalValue] = useState<T>(defaultValue);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Hydration payload staged here so async preference reads never call setState
  // from a microtask that races @dnd-kit / Radix insertion effects (Phase 1).
  const [hydrationPayload, setHydrationPayload] = useState<T | null | undefined>(undefined);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);
  // Capture the latest defaultValue in a ref so the load effect can merge it
  // over a persisted (possibly partial) blob without re-subscribing when
  // callers pass an inline default object (new ref each render).
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;
  // Capture the latest value that was queued for save so we can flush it
  // synchronously on unmount (audit finding HIGH #5 — debounced saves were
  // being silently dropped if the component unmounted mid-window).
  const pendingValueRef = useRef<T | null>(null);

  useEffect(() => {
    if (hydrationPayload === undefined) return;
    if (hydrationPayload !== null) {
      setInternalValue(hydrationPayload);
    }
    setIsLoaded(true);
    loadedRef.current = true;
    setHydrationPayload(undefined);
  }, [hydrationPayload]);

  // Load on mount
  useEffect(() => {
    if (!tenantId || !userId) {
      setIsLoaded(true);
      loadedRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const pref = await getLayoutPreference(tenantId, userId, key, 'user_pref');
        if (cancelled) return;
        if (pref && pref.visible_keys && 'value' in pref.visible_keys) {
          const merged = mergePersistedPreference(
            defaultValueRef.current,
            pref.visible_keys.value as T,
          );
          setHydrationPayload(merged);
          return;
        }
        setHydrationPayload(null);
      } catch {
        if (!cancelled) setHydrationPayload(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, userId, key]);

  // Debounced save
  const scheduleSave = useCallback(
    (next: T) => {
      if (!loadedRef.current || !tenantId || !userId) return;
      pendingValueRef.current = next;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          await saveUserLayoutPreference(
            tenantId,
            userId,
            key,
            'user_pref',
            { value: next } as unknown as Record<string, boolean>, // stored in jsonb
            [], // key_order unused for user_pref
          );
          pendingValueRef.current = null; // flushed
        } catch {
          // Silently fail — local state is still correct for this session
        } finally {
          setIsSaving(false);
        }
      }, debounceMs);
    },
    [tenantId, userId, key, debounceMs],
  );

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setInternalValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        scheduleSave(resolved);
        return resolved;
      });
    },
    [scheduleSave],
  );

  const reset = useCallback(async () => {
    if (!tenantId || !userId) return;
    setInternalValue(defaultValue);
    try {
      await resetUserPreference(tenantId, userId, key, 'user_pref');
    } catch {
      // Silently fail
    }
  }, [tenantId, userId, key]);

  // Cleanup timer on unmount AND flush any pending save so user changes
  // aren't silently lost mid-debounce. The flush is fire-and-forget — we
  // can't await inside a cleanup function, but scheduling the write without
  // debouncing is strictly better than dropping it.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const pending = pendingValueRef.current;
      if (pending !== null && tenantId && userId && loadedRef.current) {
        void saveUserLayoutPreference(
          tenantId,
          userId,
          key,
          'user_pref',
          { value: pending } as unknown as Record<string, boolean>,
          [],
        );
      }
    };
  }, [tenantId, userId, key]);

  return { value, setValue, reset, isLoaded, isSaving };
}
